import React from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  IconButton,
  TableFooter,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';

import { ICompany } from '../../../../../interfaces/companies';
import { SHeadTableCell } from '../../../../../components/styledComponents/Tables';
import TablePaginator from '../../../../../components/TablePaginator/TablePaginator';

interface IProps {
  companies: ICompany[];
  page: number;
  perPage: number;
  total: number;
  onConnect?: (company: ICompany) => void;
  fetch?: (data: { page: number; perPage: number }) => void;
  hideEdit?: boolean;
  hidePagination?: boolean;
  connectedCompanies?: number[];
}

const useStyles = makeStyles()(() => {
  return {
    table: {
      marginBottom: 20,
    },
    editBtn: {
      marginRight: 5,
    },
    rowItems: {
      fontSize: '17px !important',
    },
  };
});

const CompaniesTable: React.FC<IProps> = ({
  companies,
  page,
  perPage,
  total,
  onConnect,
  fetch,
  hideEdit,
  hidePagination,
  connectedCompanies,
}) => {
  const classes1 = useStyles();
  const { classes } = classes1;
  const intl = useIntl();
  const navigate = useNavigate();

  return (
    <div className={classes.table}>
      <Table aria-label='CompaniesTable'>
        <TableHead>
          <TableRow>
            <SHeadTableCell className={classes.rowItems}>
              {intl.formatMessage({ id: 'COMPANY.TABLE.ID' })}
            </SHeadTableCell>
            <SHeadTableCell className={classes.rowItems}>
              {intl.formatMessage({ id: 'COMPANY.TABLE.NAME' })}
            </SHeadTableCell>
            <SHeadTableCell className={classes.rowItems}>
              {intl.formatMessage({ id: 'COMPANY.TABLE.LEGAL_PERSON' })}
            </SHeadTableCell>
            <SHeadTableCell className={classes.rowItems}>
              {intl.formatMessage({ id: 'COMPANY.TABLE.INN' })}
            </SHeadTableCell>
            <SHeadTableCell align='right' className={classes.rowItems}>
              {intl.formatMessage({ id: 'COMPANY.TABLE.ACTIONS' })}
            </SHeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {companies.map(company => (
            <TableRow key={company.id}>
              <TableCell component='th' scope='row' className={classes.rowItems}>
                {company.id}
              </TableCell>
              <TableCell className={classes.rowItems}>{company.brand}</TableCell>
              <TableCell className={classes.rowItems}>{company.name}</TableCell>
              <TableCell className={classes.rowItems}>{company.inn}</TableCell>
              <TableCell align='right'>
                <div style={{ minWidth: 145 }}>
                  {!hideEdit && (
                    <IconButton
                      className={classes.editBtn}
                      color='primary'
                      aria-label='previous'
                      onClick={() => navigate(`/companies/edit/${company.id}`)}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  {onConnect && (
                    <IconButton onClick={() => onConnect(company)}>
                      {connectedCompanies && connectedCompanies.includes(company.id) ? (
                        <LinkOffIcon color='error' />
                      ) : (
                        <LinkIcon color='primary' />
                      )}
                    </IconButton>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        {!hidePagination && (
          <TableFooter>
            <TableRow>
              <TablePaginator
                page={page}
                perPage={perPage}
                fetchRows={fetch}
                total={total}
                label={intl.formatMessage({ id: 'COMPANY.TABLE.TITLE' })}
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </div>
  );
};

export default CompaniesTable;
